import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Space, Spin, Table, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useFortiusClientService } from "../../services/fortius/FortiusClientServiceProvider";
import { ClientSummary } from "../../model/fortius/ClientSummary";

const { Title, Paragraph } = Typography;

// Super-admin landing page: lists every tenant in the system and offers a CTA
// into the create-client wizard. Routed at /fortius/clients.
export const SuperAdminClientList: React.FC = () => {
    const service = useFortiusClientService();
    const navigate = useNavigate();

    const [clients, setClients] = useState<ClientSummary[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        service.listClients()
            .then((rows) => {
                if (cancelled) return;
                setClients(rows);
                setError(null);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message ?? 'Failed to load clients.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [service]);

    return (
        <Card data-testid="super-admin-client-list">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <div>
                        <Title level={3} style={{ marginBottom: 0 }}>Fortius Clients</Title>
                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            All tenants on the platform. Only visible to Fortius super-admins.
                        </Paragraph>
                    </div>
                    <Button
                        type="primary"
                        data-testid="super-admin-create-client-btn"
                        onClick={() => navigate('/fortius/clients/new')}
                    >
                        Create new client
                    </Button>
                </Space>

                {error && <Alert type="error" message={error} data-testid="super-admin-client-list-error" />}

                {loading ? (
                    <Spin data-testid="super-admin-client-list-loading" />
                ) : (
                    <Table<ClientSummary>
                        rowKey="id"
                        data-testid="super-admin-client-list-table"
                        dataSource={clients}
                        pagination={false}
                        columns={[
                            {
                                title: 'Name',
                                dataIndex: 'name',
                                render: (name: string, row: ClientSummary) => (
                                    <Link to={`/fortius/clients/${row.id}/settings`} data-testid={`super-admin-client-row-${row.id}`}>
                                        {name}
                                    </Link>
                                ),
                            },
                            { title: 'Description', dataIndex: 'description', render: (d: string | null) => d ?? '—' },
                        ]}
                    />
                )}
            </Space>
        </Card>
    );
};
